import React, { memo, useCallback, useMemo } from 'react';
import {
  ListItem,
  Tooltip,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useLocation, matchPath } from 'react-router-dom';
import { useFeatureFlag, useMenuState } from 'modules/common/hooks';
import { useAvatar } from 'modules/App/hooks/useAvatar';
import { useMeContext } from 'modules/me';
import { AppMenu } from 'modules/App/menu';
import { useSidePanelContext } from './SidePanelContext';

type StyleProps = {
  isTiwScreenReaderEnabled: boolean;
};

const useStyles = makeStyles((theme) => ({
  listItem: {
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.dark
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.dark
    },
    '&.Mui-selected,.Mui-selected:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.dark
    },
    position: 'absolute',
    bottom: theme.spacing(3)
  },
  avatar: {
    backgroundColor: theme.palette.success.main,
    color: ({ isTiwScreenReaderEnabled }: StyleProps) =>
      isTiwScreenReaderEnabled
        ? theme.palette.getContrastText(theme.palette.success.main)
        : theme.palette.excluded.contrastText,
    width: theme.spacing(4),
    height: theme.spacing(4)
  },
  menuText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    fontSize: theme.typography.body2.fontSize,
    fontFamily: theme.typography.h6.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    ...theme.typography.body1,
    marginLeft: theme.spacing(1.5)
  },
  iconButton: {
    padding: theme.spacing(0)
  },
  listItemIcon: {
    minWidth: theme.spacing(0),
    padding: theme.spacing(0)
  }
}));

type UserProfileProps = {
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const UserProfile: React.FC<UserProfileProps> = ({
  setDrawerOpen
}: UserProfileProps) => {
  const { pinned = false } = useSidePanelContext();
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  const me = useMeContext();
  const location = useLocation();
  const classes = useStyles({ isTiwScreenReaderEnabled });
  const listItemClasses = useMemo(
    () => ({
      root: classes.listItem
    }),
    [classes.listItem]
  );
  const listItemTextClasses = useMemo(
    () => ({
      primary: classes.menuText
    }),
    [classes.menuText]
  );
  const { anchorEl, onMenuClose, onMenuClick } = useMenuState();

  const { avatar } = useAvatar({
    firstName: me?.firstName,
    lastName: me?.lastName
  });

  const handleMenuClose = useCallback(() => {
    onMenuClose();
    setDrawerOpen(pinned);
  }, [setDrawerOpen, onMenuClose, pinned]);

  return (
    <>
      <ListItem
        id="profile-menu-icon"
        classes={listItemClasses}
        button
        selected={!!matchPath(location.pathname, '/profile')}
        onClick={onMenuClick}
        dense={false}
        data-testid={'profile-button'}
      >
        {isTiwScreenReaderEnabled ? (
          <ListItemIcon
            color="primary"
            className={classes.listItemIcon}
            data-testid={'avatar'}
          >
            <Avatar className={classes.avatar}>{avatar}</Avatar>
          </ListItemIcon>
        ) : (
          <IconButton
            size="medium"
            color="primary"
            className={classes.iconButton}
            data-testid={'avatar'}
          >
            <Avatar className={classes.avatar}>{avatar}</Avatar>
          </IconButton>
        )}

        {me?.displayText && (
          <Tooltip title={me.displayText}>
            <ListItemText
              primary={me.displayText}
              classes={listItemTextClasses}
            />
          </Tooltip>
        )}
      </ListItem>

      <AppMenu
        data-testid={'app-menu'}
        anchorEl={anchorEl}
        onMenuClose={handleMenuClose}
        onMenuClick={onMenuClick}
      />
    </>
  );
};

export default memo(UserProfile);
